class ProductGrid extends HTMLElement {
  #eCommerceWarehouseLocationId = null;

  constructor() {
    super();

    this.loading = false;
    this.gridOuter = null;
        
    this.bindHandleScroll = this.handleScroll.bind(this);
  }

  async connectedCallback() {
    this.cacheElements();
    this.attachEventListeners();

    await this.getStoreLocations();

    const products = this.querySelectorAll('.grid-item');
    this.updateSoldOutBadges(products);
  }

  disconnectedCallback() {
    this.detachEventListeners();
  }

  cacheElements() {
    this.gridOuter = this.querySelector('.grid-outer');
  }

  attachEventListeners() {
    window.addEventListener('scroll', this.debounce(this.bindHandleScroll, 300));
  }

  detachEventListeners() {
    window.removeEventListener('scroll', this.bindHandleScroll);
  }

  async getStoreLocations() {
    try {
      const response = await fetch(
        `/apps/inventory-availability/locations`,
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const storeLocations = await response.json();

      if (!storeLocations || storeLocations.length === 0) return;

      let eCommerceWarehouseLocationId = storeLocations.find(location => location.name === 'eCommerce Winterlingen Stock')?.id?.toString() ?? null;
      if (eCommerceWarehouseLocationId) {
        eCommerceWarehouseLocationId = eCommerceWarehouseLocationId?.split('/').pop().toString();
      }

      this.#eCommerceWarehouseLocationId = eCommerceWarehouseLocationId;
    } catch (error) {
      console.error('Error fetching store locations:', error);
    }
  }

  loadMoreProducts() {
    if (this.loading) return;

    const loadMoreBtn = this.querySelector('#load-more');
    if (!loadMoreBtn) return;

    const productGrid = this.querySelector('#CollectionLoop');

    const nextPageUrl = loadMoreBtn.getAttribute('href');
    if (!nextPageUrl || nextPageUrl === '') return;

    this.toggleLoadingState(true);

    fetch(nextPageUrl)
      .then(response => response.text())
      .then(data => {
        const parser = new DOMParser();
        const htmlDocument = parser.parseFromString(data, 'text/html');
        const newProducts = htmlDocument.querySelectorAll('#CollectionLoop .grid-item');
        const nextLoadMoreBtn = htmlDocument.querySelector('#load-more');

        // Append new products to the grid
        newProducts.forEach(product => {
          productGrid.appendChild(product);
        });

        // Update the "load-more" button for the next page
        if (nextLoadMoreBtn) {
          loadMoreBtn.setAttribute('href', nextLoadMoreBtn.getAttribute('href'));
        } else {
          loadMoreBtn.remove(); // No more pages
        }
        
        this.toggleLoadingState(false);

        this.updateSoldOutBadges(newProducts);
      })
      .catch(error => {
        console.error('Error loading products:', error);
        this.toggleLoadingState(false);
      });
  }

  handleScroll() {
    if (this.loading) return;

    const loadMoreBtn = this.querySelector('#load-more');
    if (!loadMoreBtn) return;
  
    const rect = loadMoreBtn.getBoundingClientRect();
    if (rect.top <= window.innerHeight) {
      this.loadMoreProducts();
    }
  }

  toggleLoadingState(loading) {
    this.loading = loading;
    if (!this.gridOuter) return;

    if (loading) {
      this.gridOuter.classList.add('is-loading');
    } else {
      this.gridOuter.classList.remove('is-loading');
    }
  }

  debounce(func, delay) {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  };

  async updateSoldOutBadges(newProducts) {
    if (!this.#eCommerceWarehouseLocationId) return;

    await Promise.all(Array.from(newProducts).map(async (product) => {
      const productHandle = product?.getAttribute('data-product-handle');

      const available = await this.#getAvailabilitiesPerOption(productHandle);
      if (available !== null) {
        const productLinkElem = product.querySelector('.product-item__image .product-link');
        const productBadgeElem = product.querySelector('.badge-box.badge-box--soldout');

        if (available) {    // Available — remove badge if it exists
          productBadgeElem?.remove();
        } else {            // Sold out — update or add badge
          if (productBadgeElem) {
            productBadgeElem.textContent = window.theme.lang.sold_out;
          } else if (productLinkElem) {
            productLinkElem.insertAdjacentHTML(`afterend`, `
              <span class="badge-box align--top-left aos-animate" data-aos="fade" data-aos-delay="200" data-aos-duration="800" data-aos-anchor="#product-item--template--25348255580484__main-15092528709956">
                ${window.theme.lang.sold_out}
              </span>
            `);
          }
        }
      }
    }));
  }

  async #getAvailabilitiesPerOption(productHandle) {
    if (!this.#eCommerceWarehouseLocationId) return null;
    if (!productHandle) return null;

    let available = false;

    try {
      const response = await fetch(
        `/apps/inventory-availability/availabilities-using-product`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            productHandle
          })
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();

      data.variants.edges.forEach(edge => {
        const variant = edge.node;

        const inventoryLevels = variant?.inventoryItem?.inventoryLevels ?? [];
        inventoryLevels.edges.forEach(edge => {
          const locationGid = edge?.node?.location?.id ?? null;
          const locationId = locationGid?.split('/').pop().toString();

          if (locationId === this.#eCommerceWarehouseLocationId) {
            const quantity = edge?.node?.quantities?.[0]?.quantity;

            if (quantity > 0) {
              available = true;
            }
          }
        });
      });
    } catch (error) {
      console.error('Error fetching variant availabilities per option (size picker):', error);

      return null;
    }

    return available;
  }
}

if (!customElements.get('product-grid')) {
  customElements.define('product-grid', ProductGrid);
}